import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

// Base64-url encoder for customer identifier — mirrors backend _encode_identifier.
const encodeCustomerId = (s) => {
  if (!s) return null;
  const b64 = btoa(unescape(encodeURIComponent(s)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatMx } from '../utils/datetime';
import { genesysInteractionUrl } from '../utils/genesys';
import { MessageSquare, Printer } from 'lucide-react';
import { getCallDetail, setTypificationOverride, corregirAprovechamiento, descartarErrorBoleta, getStoredUser } from '../api';
import ChatPanel from '../components/ChatPanel';
import { isOutboundCall, directionLabel } from '../utils/direction';
import { OutboundBadge, BoletaNoAplica, CHECKLIST_SOLAPA_GUIA } from '../components/OutboundBoleta';
import BoletaV2, { CalificacionV2 } from '../components/BoletaV2';
import {
  BoletaSinEvidencia,
  esNoAprovechable,
  CorregirAprovechamiento,
  EstadoAprovechamiento,
} from '../components/AprovechableBoleta';
import CriteriosDeLaGuia from '../components/CriteriosDeLaGuia';
import ReplicaPanel from '../components/ReplicaPanel';
import PanelDeCorrecciones from '../components/PanelDeCorrecciones';

const SPEAKER_CLASSES = ['speaker-0', 'speaker-1', 'speaker-2', 'speaker-3'];

const scoreColor = (s) => {
  if (s >= 75) return '#34d399';
  if (s >= 50) return '#fbbf24';
  if (s >= 25) return '#fb923c';
  return '#f87171';
};

const CHECKLIST_LABELS = {
  saludo: 'Saludo institucional',
  sondeo: 'Sondeo de necesidades',
  despedida: 'Despedida institucional',
  interes: 'Mostró interés',
  concreto_venta: 'Concretó venta',
  cotizacion: 'Ofreció cotización',
  cierre_venta: 'Cierre de venta',
  alternativas: 'Ofreció alternativas',
  informacion: 'Brindó información',
  confirmacion: 'Confirmó datos',
  personalizacion: 'Personalizó atención',
  politicas: 'Explicó políticas',
  vocabulario: 'Vocabulario adecuado',
  tiempo_espera: 'Manejo tiempo espera',
  amabilidad: 'Trato amable',
  error: 'Cometió error',
  atencion_espera: 'Atención en espera',
};

const URGENCY_COLORS = { low: '#34d399', medium: '#fbbf24', high: '#fb923c', critical: '#f87171' };

const SENTIMENT_COLORS = {
  positive: '#34d399',
  neutral: '#6b7280',
  negative: '#f87171',
};

function SentimentTimeline({ segments }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title">
        Curva de sentimiento por segmento{' '}
        <span
          style={{ opacity: 0.5, cursor: 'help', marginLeft: 4 }}
          title={
            'El LLM marca hasta 8 puntos cronológicos donde detecta picos o cambios de sentimiento. '
            + 'Cada segmento indica si habla el agente o el cliente, qué tan positivo/negativo es '
            + '(score -1 a +1) y muestra la frase del transcript que sustenta la lectura.'
          }
        >
          ⓘ
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {segments.map((seg, i) => {
          const color = SENTIMENT_COLORS[seg.sentiment] || SENTIMENT_COLORS.neutral;
          const speakerLabel = seg.speaker === 0 ? 'Agente' : seg.speaker === 1 ? 'Cliente' : `Speaker ${seg.speaker}`;
          const score = typeof seg.score === 'number' ? seg.score.toFixed(2) : '—';
          return (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 90px 1fr 50px',
                gap: 10,
                alignItems: 'center',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.02)',
                borderLeft: `3px solid ${color}`,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <span style={{ color: '#9ca3af', fontWeight: 600 }}>#{i + 1}</span>
              <span style={{ color }}>
                {speakerLabel} · {seg.sentiment}
              </span>
              <span style={{ color: '#c4b5fd', fontStyle: 'italic' }}>"{seg.excerpt || '—'}"</span>
              <span style={{ color: '#6b7280', textAlign: 'right', fontFamily: 'monospace' }}>{score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Un grupo de datos del encabezado, con su rótulo.
 *
 * Los nombres son LOS MISMOS que los grupos de la barra de filtros
 * (`AnalyticsFilterBar`), y eso es el punto: quien filtra por "Atención" y luego
 * abre una llamada encuentra ahí "Atención" con los mismos campos. Dos
 * vocabularios para lo mismo obliga a traducir mentalmente en cada salto.
 *
 * El encabezado tenía CATORCE campos en una rejilla plana de columnas de 150 px,
 * en orden de acumulación. Además del desorden, producía el traslape que se
 * arregló en `App.css`: un nombre de cola largo se desbordaba encima de Hotel.
 */
function GrupoDeDatos({ titulo, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        // 12px y #a1a8b5 (8.0:1). Antes: 9.5px y #4b5563, o sea 2.53:1 — el
        // rótulo que organiza la sección era lo MENOS visible de la pantalla.
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.4,
        color: '#a1a8b5',
        marginBottom: 4,
        paddingBottom: 3,
        borderBottom: '1px solid #26262e',
        maxWidth: 1000,
      }}>
        {titulo}
      </div>
      <div className="detail-meta" style={{ marginBottom: 0 }}>
        {children}
      </div>
    </div>
  );
}


function AnalysisSection({
  analysis, boletaV2 = null, outbound = false, onCorregir = null, guardando = false,
  callId = null, replica = null, onDescartarError = null,
  puedeComentar = false, onReplicaComentada = null, typification = null,
}) {
  if (!analysis) return null;

  // Dos motivos distintos para no tener boleta, con el mismo efecto en pantalla:
  // no se muestra score ni los nueve criterios. Ver `app/boleta.py` y
  // `app/aprovechable.py`.
  // El veredicto EFECTIVO: la corrección de un coordinador gana sobre el
  // automático, y el backend ya lo resolvió en `aprovechable_efectivo`.
  const noAprovechable = analysis.aprovechable_efectivo === false;
  const sinBoleta = outbound || noAprovechable;

  const checklist = analysis.checklist || {};
  const actionItems = typeof analysis.action_items === 'string' ? JSON.parse(analysis.action_items || '[]') : (analysis.action_items || []);
  const keyQuotes = typeof analysis.key_quotes === 'string' ? JSON.parse(analysis.key_quotes || '[]') : (analysis.key_quotes || []);

  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#a5b4fc' }}>Analisis IA</h2>

      {/* En salientes la boleta no aplica: se explica en lugar de mostrar un
          score vacío, que se leería como "el agente sacó cero". */}
      {outbound && <BoletaNoAplica compact />}
      {noAprovechable && <BoletaSinEvidencia analysis={analysis} compact />}

      {/* Corrección manual: sólo administradores. El criterio se equivoca y su
          sesgo es conservador a propósito, así que hace falta poder corregirlo
          en las dos direcciones — y se decide leyendo la transcripción, que
          está en esta misma pantalla. */}
      {onCorregir && (
        <div
          className="card"
          style={{ marginTop: noAprovechable || outbound ? 8 : 0, paddingTop: 4 }}
        >
          <CorregirAprovechamiento
            analysis={analysis}
            onCorregir={onCorregir}
            guardando={guardando}
          />
        </div>
      )}

      {/* Score + Satisfaction + Classification */}
      <div className="analysis-top-row">
        <div className="analysis-score-card card">
          {sinBoleta ? (
            <div className="analysis-score-circle" style={{ borderColor: '#4b5563' }}>
              <span className="analysis-score-value" style={{ color: '#6b7280', fontSize: 20 }}>N/A</span>
              <span className="analysis-score-label">Sin score</span>
            </div>
          ) : (
            <div className="analysis-score-circle" style={{ borderColor: scoreColor(analysis.quality_score || 0) }}>
              <span className="analysis-score-value" style={{ color: scoreColor(analysis.quality_score || 0) }}>
                {analysis.quality_score ?? '-'}
              </span>
              <span className="analysis-score-label">Score</span>
            </div>
          )}
          {/* La v2 al lado de la v1, no en su lugar. Son escalas distintas
              —0-100 contra 0-10— y se muestran juntas a propósito: mientras
              Gerencia decide cuál manda, poder compararlas sobre la misma
              llamada ES la evidencia. Si la llamada no tiene boleta v2 (antes
              del 1-ago-2026) el componente no pinta nada. */}
          {!sinBoleta && <CalificacionV2 boleta={boletaV2} />}
          <div className="analysis-satisfaction">
            {'★'.repeat(analysis.customer_satisfaction || 0)}
            {'☆'.repeat(5 - (analysis.customer_satisfaction || 0))}
            <span style={{ marginLeft: 6, color: '#6b7280', fontSize: 11 }}>Satisfaccion</span>
          </div>
        </div>

        <div className="card" style={{ flex: 1 }}>
          <div className="card-title">Clasificacion</div>
          <div className="analysis-badges">
            {analysis.call_category && <span className="analysis-badge analysis-badge-category">{analysis.call_category}</span>}
            {analysis.conclusion_code && <span className="analysis-badge analysis-badge-conclusion">{analysis.conclusion_code}</span>}
            {analysis.is_spam && <span className="analysis-badge analysis-badge-spam">SPAM</span>}
            {analysis.urgency_level && (
              <span className="analysis-badge" style={{ background: `${URGENCY_COLORS[analysis.urgency_level]}22`, color: URGENCY_COLORS[analysis.urgency_level] }}>
                {analysis.urgency_level.toUpperCase()}
              </span>
            )}
            {analysis.escalation_risk && <span className="analysis-badge analysis-badge-spam">Riesgo escalacion</span>}
            {analysis.follow_up_needed && <span className="analysis-badge" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>Seguimiento</span>}
          </div>
          {analysis.call_reason && <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 8 }}>Motivo: {analysis.call_reason}</div>}
          {analysis.conclusion && <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>Conclusion: {analysis.conclusion}</div>}
        </div>
      </div>


      {/* Summary ES */}
      {analysis.summary_es && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">Resumen IA</div>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: '#c4b5fd' }}>{analysis.summary_es}</p>
        </div>
      )}

      {/* Sentiment timeline */}
      {Array.isArray(analysis.sentiment_segments) && analysis.sentiment_segments.length > 0 && (
        <SentimentTimeline segments={analysis.sentiment_segments} />
      )}

      <div className="charts-grid">
        {/* Business data */}
        <div className="card">
          <div className="card-title">Datos de negocio</div>
          <div className="analysis-business-grid">
            {(analysis.customer_phone || analysis.customer_email) && (() => {
              const ident = analysis.customer_phone || analysis.customer_email;
              const encoded = encodeCustomerId(ident);
              return (
                <div>
                  <span className="meta-label">
                    Cliente {analysis.is_repeat_customer && <span style={{ color: '#a5b4fc' }}>· recurrente</span>}
                  </span>
                  <div className="meta-value">
                    <Link to={`/customers/${encoded}`} style={{ color: '#a5b4fc' }}>
                      {analysis.customer_phone || analysis.customer_email}
                    </Link>
                  </div>
                </div>
              );
            })()}
            {analysis.complaints && (
              <div>
                <span className="meta-label">Quejas</span>
                <div className="meta-value" style={{ color: '#f87171', fontSize: 13 }}>
                  {analysis.complaints}
                </div>
              </div>
            )}
            {analysis.hotel_name && <div><span className="meta-label">Hotel</span><div className="meta-value">{analysis.hotel_name}</div></div>}
            {analysis.amount && <div><span className="meta-label">Monto</span><div className="meta-value">${analysis.amount} {analysis.currency || ''}</div></div>}
            {analysis.checkin_date && <div><span className="meta-label">Check-in</span><div className="meta-value">{analysis.checkin_date}</div></div>}
            {analysis.checkout_date && <div><span className="meta-label">Check-out</span><div className="meta-value">{analysis.checkout_date}</div></div>}
            {analysis.guest_name && <div><span className="meta-label">Huesped</span><div className="meta-value">{analysis.guest_name}</div></div>}
            {analysis.num_guests && <div><span className="meta-label"># Personas</span><div className="meta-value">{analysis.num_guests}</div></div>}
            {analysis.travel_reason && <div><span className="meta-label">Motivo viaje</span><div className="meta-value">{analysis.travel_reason}</div></div>}
            {analysis.payment_method && <div><span className="meta-label">Forma pago</span><div className="meta-value">{analysis.payment_method}</div></div>}
            {analysis.rate_type && <div><span className="meta-label">Tarifa</span><div className="meta-value">{analysis.rate_type}</div></div>}
            <div><span className="meta-label">Tipo tarifa/cliente</span><div className="meta-value">{analysis.customer_tariff_type || <span style={{ color: '#6b7280', fontStyle: 'italic' }}>pendiente de análisis</span>}</div></div>
            {analysis.is_spam && <div><span className="meta-label">Subtipo spam</span><div className="meta-value">{analysis.spam_subtype || <span style={{ color: '#6b7280', fontStyle: 'italic' }}>pendiente de análisis</span>}</div></div>}
            {analysis.reservation_code && <div><span className="meta-label">Cod. reservacion</span><div className="meta-value">{analysis.reservation_code}</div></div>}
            {analysis.customer_origin && <div><span className="meta-label">Origen cliente</span><div className="meta-value">{analysis.customer_origin}</div></div>}
            {analysis.restaurant_name && <div><span className="meta-label">Restaurante</span><div className="meta-value">{analysis.restaurant_name}</div></div>}
          </div>
        </div>

        {/* Checklist */}
        <div className="card">
          <div className="card-title">Checklist de calidad</div>
          <div className="analysis-checklist">
            {Object.entries(CHECKLIST_LABELS).map(([key, label]) => {
              const val = checklist[key];
              const isError = key === 'error';
              let icon, cls;
              if (val === true) {
                icon = isError ? '✗' : '✓';
                cls = isError ? 'chk-fail' : 'chk-pass';
              } else if (val === false) {
                icon = isError ? '✓' : '✗';
                cls = isError ? 'chk-pass' : 'chk-fail';
              } else {
                icon = '—';
                cls = 'chk-na';
              }
              // En salientes, los chk_* que comparten nombre con un criterio
              // de la guía se atenúan: el aviso de arriba dice que la guía no
              // aplica, y verlos en verde se lee como una contradicción.
              // En una llamada no aprovechable NINGÚN chk_* significa nada: el
              // modelo los evaluó sobre una transcripción sin diálogo. Se atenúan
              // todos, no sólo los cuatro que solapan con la guía.
              const solapaGuia = noAprovechable || (outbound && CHECKLIST_SOLAPA_GUIA.has(key));
              return (
                <div
                  key={key}
                  className={`chk-item ${solapaGuia ? 'chk-na' : cls}`}
                  style={solapaGuia ? { opacity: 0.45 } : undefined}
                  title={
                    solapaGuia
                      ? (noAprovechable
                          ? 'Sin valor: no hubo intercambio humano que evaluar en esta llamada.'
                          : 'Detección secundaria, no la guía de nueve criterios: esa no aplica a llamadas salientes.')
                      : undefined
                  }
                >
                  <span className="chk-icon">{solapaGuia ? '—' : icon}</span>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Key quotes */}
      {keyQuotes.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Citas clave</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {keyQuotes.map((q, i) => (
              <div key={i} style={{ fontSize: 13, color: '#c4b5fd', borderLeft: '3px solid #818cf8', paddingLeft: 12, fontStyle: 'italic' }}>
                "{q}"
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action items */}
      {actionItems.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Acciones pendientes</div>
          <ul style={{ paddingLeft: 20, fontSize: 13, color: '#e0e0e6' }}>
            {actionItems.map((a, i) => <li key={i} style={{ marginBottom: 4 }}>{a}</li>)}
          </ul>
        </div>
      )}

      {/* Los nueve criterios con su cita. Antes se pintaban aquí a mano, en un
          formato distinto del que veía el agente (que de hecho NO los veía).
          Ahora es el MISMO componente para los dos: si difirieran, la sesión de
          retroalimentación arrancaría discutiendo la herramienta. En salientes
          no se muestran — la explicación ya está arriba y repetirla como nueve
          "no aplica" entierra el resto del análisis. */}
      {!sinBoleta && (
        <CriteriosDeLaGuia validators={analysis.validators} titulo="Validadores CRECE — los nueve criterios" />
      )}

      {/* La boleta v2, DEBAJO de la v1. El orden importa: la de arriba es la
          calificación vigente, la de abajo la propuesta. */}
      {!sinBoleta && (
        <BoletaV2
          boleta={boletaV2}
          onDescartar={onDescartarError}
          callId={callId}
          puedeCorregir={puedeComentar}
          onCorregido={onReplicaComentada}
        />
      )}

      {/* Las correcciones humanas, juntas y con nombre. Va DEBAJO de la boleta
          y ANTES de la réplica: primero la evaluación con su evidencia, luego
          qué se le corrigió, luego lo que el agente responde. */}
      {!sinBoleta && (
        <PanelDeCorrecciones
          analysis={analysis}
          typification={typification}
          boleta={boletaV2}
          replica={replica}
        />
      )}

      {/* Lo que el agente respondió (#99). Sólo lectura: la réplica es su voz, y
          un supervisor que pudiera escribirla haría que dejara de significar
          nada. Ver el bloque de `guardar_replica` en routers/calls.py. */}
      {!sinBoleta && (
        <ReplicaPanel
          callId={callId}
          replica={replica}
          puedeEscribir={false}
          puedeComentar={puedeComentar}
          onGuardada={onReplicaComentada}
          viewerMode="admin"
        />
      )}
    </div>
  );
}

export default function CallDetail() {
  const { id } = useParams();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const esAdmin = getStoredUser()?.role === 'admin';

  // Se recarga el detalle completo en vez de parchear el estado local: al
  // corregir, el backend también restaura score, criterios y tipificación desde
  // `raw_response`, y adivinar aquí cuáles cambiaron es cómo la pantalla acaba
  // discrepando de la base.
  const corregir = (valor) => {
    setGuardando(true);
    corregirAprovechamiento(id, valor)
      .then(() => getCallDetail(id))
      .then((r) => setCall(r.data))
      .catch((e) => {
        console.error('No se pudo corregir la clasificación', e);
        window.alert('No se pudo guardar la corrección. Revisa tu sesión e inténtalo de nuevo.');
      })
      .finally(() => setGuardando(false));
  };

  // Descartar la penalización por error, o deshacerlo. Devuelve la promesa para
  // que el componente muestre su propio "..." y su propio error: el `alert` de
  // `corregir` esconde el detalle del servidor, y aquí ese detalle importa
  // ("esta boleta no penaliza por error", "no hay boleta v2").
  //
  // Se recarga el detalle completo en vez de parchar `boleta_v2` con la
  // respuesta: descartar el error mueve la calificación, y esa nota se pinta
  // también en el encabezado. Adivinar aquí qué campos cambiaron es cómo la
  // pantalla acaba discrepando de la base — el mismo argumento que en
  // `corregir`.
  // Recarga el detalle completo. Lo usa el comentario a la réplica: la
  // respuesta del endpoint ya trae el hilo, pero recargar es lo que garantiza
  // que la pantalla y la base digan lo mismo — el mismo argumento que en
  // `corregir` y en `onDescartarError`.
  const recargar = () => getCallDetail(id).then((r) => setCall(r.data));

  const onDescartarError = (esError, nota) =>
    descartarErrorBoleta(id, esError, nota)
      .then(() => getCallDetail(id))
      .then((r) => setCall(r.data));

  useEffect(() => {
    getCallDetail(id).then(r => { setCall(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading">Cargando detalle...</div>;
  if (!call) return <div className="loading">Llamada no encontrada</div>;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const sentimentTimeline = call.sentiments.map((s, i) => ({
    index: i,
    score: s.sentiment_score,
    text: s.segment_text?.slice(0, 40),
    sentiment: s.sentiment,
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/calls" className="back-link">&larr; Volver a llamadas</Link>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {/* La boleta como documento para la sesión (#98). Enlace y no botón:
            se abre en otra pestaña porque el supervisor imprime desde ahí y no
            quiere perder el detalle que estaba leyendo.

            Sólo cuando hay análisis: en una llamada sin audio la boleta saldría
            en blanco, y un documento vacío se lee como una falla. */}
        {call.analysis && (
        <Link
          to={`/calls/${call.id}/boleta`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            border: '1px solid #2d2d44', color: '#a5b4fc', textDecoration: 'none',
            borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
          }}
        >
          <Printer size={15} /> Boleta para la sesión
        </Link>
        )}
        {(call.transcript_text || call.speakers?.length > 0) && (
          <button
            onClick={() => setChatOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#818cf8', color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
            }}
          >
            <MessageSquare size={15} /> Indagar con IA
          </button>
        )}
        </div>
      </div>

      <h1 className="page-title">Detalle de llamada</h1>

      {/* Los datos de la llamada, agrupados por la pregunta que responden y con
          los MISMOS nombres que los grupos de la barra de filtros. Eran catorce
          campos en una rejilla plana, en orden de acumulación — y de ahí salía
          el traslape de Cola sobre Hotel que se arregló en `App.css`.

          El aprovechamiento salió de Dirección: es un juicio sobre la llamada,
          no una propiedad de su dirección. */}
      <GrupoDeDatos titulo="Temporalidad y Dirección">
        <div className="meta-item">
          <div className="meta-label">Fecha</div>
          <div className="meta-value">{formatMx(call.start_time)}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Duracion</div>
          <div className="meta-value">{call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s` : '-'}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Direccion</div>
          <div className="meta-value">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {isOutboundCall(call) ? <OutboundBadge /> : directionLabel(call)}
            </span>
          </div>
        </div>
      </GrupoDeDatos>

      <GrupoDeDatos titulo="Atención">
        <div className="meta-item">
          <div className="meta-label">Agente</div>
          <div className="meta-value">{call.agent_name || '-'}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Cola</div>
          <div className="meta-value">{call.queue_name || '-'}</div>
        </div>
        <div className="meta-item" title="Hotel derivado de la cola (catálogo BD-1).">
          <div className="meta-label">Hotel</div>
          <div className="meta-value">{call.hotel || '-'}</div>
        </div>
        <div className="meta-item" title="Centro al que se transfirió la llamada, según el número destino (BD-2).">
          <div className="meta-label">Centro de contacto</div>
          <div className="meta-value">{call.contact_center_type || 'Sin transferencia'}</div>
        </div>
      </GrupoDeDatos>

      <GrupoDeDatos titulo="Categoría de llamada">
        <div className="meta-item" title="Lo que el agente eligió al colgar, del catálogo de CRECE.">
          <div className="meta-label">Conclusion agente</div>
          <div className="meta-value">
            {call.typification?.agent
              ? <span className="analysis-badge analysis-badge-conclusion">{call.typification.agent}</span>
              // #8b93a1 (6.18:1) y no #4b5563 (2.53:1): "sin tipificar" es
              // un VALOR, no un adorno — decir que falta un dato en un gris
              // que no se lee es no decirlo.
              : <span style={{ color: '#8b93a1' }}>sin tipificar</span>}
          </div>
        </div>
        <div className="meta-item" title="Lo que determinó el análisis automático.">
          <div className="meta-label">Conclusion IA</div>
          <div className="meta-value">
            <ConclusionIA t={call.typification} callId={call.id} />
          </div>
        </div>
      </GrupoDeDatos>

      <GrupoDeDatos titulo="Calidad de atención">
        <div className="meta-item" title="Si hubo intercambio suficiente para juzgar la llamada. Ver app/aprovechable.py.">
          <div className="meta-label">Aprovechamiento</div>
          <div className="meta-value">
            {call.analysis
              ? <EstadoAprovechamiento analysis={call.analysis} />
              : <span style={{ color: '#8b93a1' }}>sin análisis</span>}
          </div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Sentimiento</div>
          <div className="meta-value">
            {/* La MISMA fuente que el listado: la estimación del LLM que
                el servidor ya agrupa. Antes leía `overall_sentiment`
                (Deepgram), que para español viene vacío — y la clase caía a
                `badge-neutral`, así que un "N/A" se pintaba con el color de
                "neutral". Ver `app/sentimiento.py`. */}
            <span className={`badge badge-${call.analysis?.sentimiento || 'neutral'}`}>
              {call.analysis?.sentimiento || 'N/A'}
            </span>
          </div>
        </div>
      </GrupoDeDatos>

      <GrupoDeDatos titulo="Llamada">
        <div className="meta-item">
          <div className="meta-label">Telefono</div>
          <div className="meta-value">{call.ani || '-'}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Idioma</div>
          <div className="meta-value">{call.detected_language?.toUpperCase() || '-'} ({((call.language_confidence || 0) * 100).toFixed(0)}%)</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Confianza</div>
          <div className="meta-value">{((call.transcription_confidence || 0) * 100).toFixed(1)}%</div>
        </div>
        <div className="meta-item" title="Identificador de la conversación en Genesys; útil para escuchar el audio en el sistema fuente.">
          <div className="meta-label">ID Genesys</div>
          <div className="meta-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }} title={call.conversation_id || ''}>
              {call.conversation_id ? `${call.conversation_id.slice(0, 8)}…` : '-'}
            </span>
            {call.conversation_id && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(call.conversation_id);
                }}
                title="Copiar ID completo al portapapeles"
                style={{
                  background: 'transparent',
                  border: '1px solid #4b5563',
                  color: '#a5b4fc',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Copiar
              </button>
            )}
            {call.conversation_id && (
              <a
                href={genesysInteractionUrl(call.conversation_id)}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir la interacción en Genesys (nueva pestaña)"
                style={{
                  background: 'transparent',
                  border: '1px solid #4b5563',
                  color: '#a5b4fc',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 11,
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
              >
                Abrir en Genesys ↗
              </a>
            )}
          </div>
        </div>
      </GrupoDeDatos>

      {call.status === 'no_audio' && (
        <div className="card" style={{ marginBottom: 20, padding: 18, borderLeft: '3px solid #fbbf24' }}>
          <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: 6 }}>
            Sin grabación disponible
          </div>
          <div style={{ color: '#9ca3af', fontSize: 13, lineHeight: 1.5 }}>
            Esta llamada se atendió pero el audio no se subió al bucket S3.
            Datos de cola, agente, duración y clasificación CRECE vienen de la API de Genesys.
            No hay transcripción ni evaluación LLM.
          </div>
        </div>
      )}

      {/* Tipificación del agente frente a la del análisis. Va FUERA del
          condicional de AnalysisSection a propósito: una llamada sin análisis
          igual tiene (o no) tipificación del agente, y ese es justamente el
          caso "sin comparación". Solo llega en la respuesta para
          administración; al rol agente no le viaja el bloque. */}

      {/* LLM Analysis section — sólo cuando hay análisis (= hay audio) */}
      {call.status !== 'no_audio' && (
        <AnalysisSection
          analysis={call.analysis}
          boletaV2={call.boleta_v2}
          outbound={isOutboundCall(call)}
          onCorregir={esAdmin ? corregir : null}
          guardando={guardando}
          callId={call.id}
          replica={call.replica}
          onDescartarError={esAdmin ? onDescartarError : null}
          puedeComentar={esAdmin}
          onReplicaComentada={recargar}
          typification={call.typification}
        />
      )}

      {call.summary && !call.analysis?.summary_es && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">Resumen (Deepgram)</div>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: '#c4b5fd' }}>{call.summary}</p>
        </div>
      )}

      <div className="charts-grid">
        <div className="card">
          <div className="card-title">Transcripcion</div>
          <div className="transcript-block">
            {call.speakers.length > 0 ? (
              call.speakers.map((s, i) => (
                <div key={i} className="transcript-turn">
                  <span className={`speaker-tag ${SPEAKER_CLASSES[s.speaker_number % 4]}`}>
                    Speaker {s.speaker_number}
                  </span>
                  <span style={{ color: '#6b7280', fontSize: 11, marginRight: 8 }}>
                    [{formatTime(s.start_time_seconds)}]
                  </span>
                  {s.transcript_text}
                </div>
              ))
            ) : (
              <p style={{ color: '#6b7280' }}>{call.transcript_text || 'Sin transcripcion'}</p>
            )}
          </div>
        </div>

        <div>
          {sentimentTimeline.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-title">Timeline de sentimiento</div>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={sentimentTimeline}>
                  <XAxis dataKey="index" tick={false} />
                  <YAxis domain={[-1, 1]} tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8, fontSize: 12 }}
                    formatter={(v, n, p) => [v.toFixed(2), p.payload.text]}
                  />
                  <Bar dataKey="score" fill="#818cf8" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {call.topics.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-title">Temas</div>
              <div>{call.topics.map((t, i) => <span key={i} className="tag">{t.topic}</span>)}</div>
            </div>
          )}

          {call.intents.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-title">Intenciones</div>
              <div>{call.intents.map((t, i) => <span key={i} className="tag tag-intent">{t.intent}</span>)}</div>
            </div>
          )}

          {call.entities.length > 0 && (
            <div className="card">
              <div className="card-title">Entidades</div>
              <div>{call.entities.map((e, i) => (
                <span key={i} className="tag tag-entity">{e.entity_type}: {e.entity_value}</span>
              ))}</div>
            </div>
          )}
        </div>
      </div>

      <ChatPanel callId={id} isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}


// --- Tipificacion: agente vs analisis automatico ----------------------------
// "Coincide" / "difiere", nunca "correcto" / "incorrecto": no existe una
// respuesta de referencia, asi que un desacuerdo no es error de nadie hasta
// que un tercero escuche la llamada.
const TYPIFICATION_VERDICT = {
  coincide: { label: 'Coinciden', color: '#94a3b8' },
  difiere: { label: 'Difieren', color: '#94a3b8' },
  no_tipifica: { label: 'El agente no tipifico', color: '#64748b' },
  sin_comparacion: { label: 'Sin analisis con que comparar', color: '#64748b' },
};

/**
 * La conclusión del análisis, compacta, para la rejilla del detalle.
 *
 * Reemplaza la tarjeta grande que estaba aparte. Rodrigo la pidió chica y junto
 * al resto de los datos de la llamada (25-ago), y en la sesión grabada Samuel
 * tardó en encontrar la anterior — si el tech lead no la halla, un agente
 * tampoco.
 *
 * Conserva la corrección manual porque Samuel la pidió explícitamente, y le
 * agrega el DESHACER que faltaba: "le van a picar, van a decir 'ay, no le
 * quería picar, lo quiero deshacer'".
 */
function ConclusionIA({ t, callId }) {
  const [estado, setEstado] = useState(t);
  const [abierto, setAbierto] = useState(false);
  const [eleccion, setEleccion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState(null);
  const esAdmin = getStoredUser()?.role === 'admin';

  if (!estado) return <span style={{ color: '#4b5563' }}>-</span>;

  const manual = estado.override;
  const automatica = estado.v1;
  // La manual GANA en la vista, y se dice que es manual: mostrar una corrección
  // sin decir que alguien la puso convierte un juicio humano en un dato que
  // apareció solo.
  const vigente = manual?.manual_code || automatica;

  // El catálogo NO se elige aquí: lo decide la fecha de la llamada y el backend
  // lo manda ya resuelto en `catalogo_vigente`. Este componente mandaba 'v1'
  // fijo mientras el desplegable ofrecía también las etiquetas del v2, así que
  // desde el 19-ago-2026 —cuando el v2 pasó a ser el catálogo vigente— toda
  // corrección real rebotaba con 422 y la pantalla decía "No se pudo guardar."
  // sin decir por qué. No se manda: lo deriva el servidor, un solo lugar
  // decide, así no hay dos que puedan discrepar.
  const catalogo = estado.catalogo_vigente || 'v1';
  const opcionesVigentes = (catalogo === 'v2' ? estado.catalog_v2 : estado.catalog) || [];
  const opcionesDelOtro = (catalogo === 'v2' ? estado.catalog : estado.catalog_v2) || [];

  const enviar = (codigo) => {
    setGuardando(true);
    setErr(null);
    setTypificationOverride(callId, codigo, '')
      .then((r) => { setEstado(r.data.typification); setAbierto(false); setEleccion(''); })
      // El detalle del servidor, no un mensaje genérico. El genérico es la razón
      // de que este bug viviera tanto: decía que algo falló y escondía qué.
      .catch((e) => setErr(e?.response?.data?.detail || 'No se pudo guardar.'))
      .finally(() => setGuardando(false));
  };

  const veredicto = TYPIFICATION_VERDICT[estado.agreement];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {vigente
          ? <span className="analysis-badge analysis-badge-conclusion">{vigente}</span>
          : <span style={{ color: '#8b93a1' }}>sin analisis</span>}
        {manual && (
          // 11px y no 9.5: un marcador de estado puede ser más chico que un
          // valor, pero 9.5px no se lee. El ámbar sí tiene contraste de sobra.
          <span style={{ fontSize: 11, color: '#fbbf24' }} title={`Corregida a mano el ${manual.created_at?.slice(0, 10)}`}>
            corregida
          </span>
        )}
        {veredicto && !manual && (
          <span style={{ fontSize: 9.5, color: veredicto.color }}>{veredicto.label}</span>
        )}
      </span>

      {esAdmin && !abierto && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-link" style={btnMini} onClick={() => setAbierto(true)}>
            Corregir
          </button>
          {manual && (
            // `null` = volver al automatico. Va como null EXPLICITO, no como
            // cuerpo vacio: ver CorreccionTipificacion en schemas.py.
            <button className="btn-link" style={btnMini} disabled={guardando}
                    onClick={() => enviar(null)}>
              Volver al automatico
            </button>
          )}
        </div>
      )}

      {esAdmin && abierto && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="filter-select" style={{ fontSize: 11, maxWidth: 230 }}
                  value={eleccion} onChange={(e) => setEleccion(e.target.value)}>
            <option value="">Elegir conclusion...</option>
            {/* Los DOS catálogos, agrupados y rotulados. Se muestran a
                propósito: una lista mutilada deja a quien corrige buscando una
                etiqueta que "desapareció", sin saber que la frontera del
                19-ago existe. Lo que no puede pasar es que el rechazo sea mudo
                — el guardado explica por qué el otro no aplica. */}
            <optgroup label={`Catálogo ${catalogo} — aplica a esta llamada`}>
              {opcionesVigentes.map((c) => <option key={c} value={c}>{c}</option>)}
            </optgroup>
            <optgroup label={`Catálogo ${catalogo === 'v2' ? 'v1' : 'v2'} — no aplica por la fecha de la llamada`}>
              {opcionesDelOtro.map((c) => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          </select>
          <button className="btn-link" style={btnMini} disabled={!eleccion || guardando}
                  onClick={() => enviar(eleccion)}>
            {guardando ? '...' : 'Guardar'}
          </button>
          <button className="btn-link" style={btnMini} onClick={() => { setAbierto(false); setEleccion(''); }}>
            Cancelar
          </button>
        </div>
      )}
      {err && <span style={{ color: '#f87171', fontSize: 10 }}>{err}</span>}
    </div>
  );
}

const btnMini = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: '#818cf8', fontSize: 10, textDecoration: 'underline',
};
